/**
 * color.ts — pure color math: median-cut dominant-color extraction, WCAG
 * contrast, and semantic palette derivation. No DOM dependency anywhere in
 * this module — it runs identically in a browser, Node, or Deno.
 */
import type {
  ContrastLevel,
  ContrastTarget,
  DeriveSemanticPaletteOptions,
  DominantColor,
  PixelSource,
  RGB,
  SemanticPalette,
} from "./types.js";

type HSL = [number, number, number];

/** Accessibility contrast targets the palette engine must satisfy. */
export const CONTRAST_LEVELS: Record<ContrastLevel, ContrastTarget> = {
  aa: { text: 4.5, accent: 3 },
  aaa: { text: 7, accent: 3 },
  high: { text: 10, accent: 4.5 },
};

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

export const hex = ([r, g, b]: RGB): string =>
  "#" + [r, g, b].map((c) => Math.round(clamp(c, 0, 255)).toString(16).padStart(2, "0")).join("");

export const hexToRgb = (h: string): RGB => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  return [r, g, b];
};

function rgbToHsl([r, g, b]: RGB): HSL {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb([h, s, l]: HSL): RGB {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const f = (t: number): number => {
    t = ((t % 1) + 1) % 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [f(h + 1 / 3) * 255, f(h) * 255, f(h - 1 / 3) * 255];
}

/** WCAG relative luminance of an sRGB triplet (0–255). */
function luminance([r, g, b]: RGB): number {
  const lin = (c: number): number => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two sRGB triplets. */
export function contrast(a: RGB, b: RGB): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

const mix = (a: RGB, b: RGB, t: number): RGB => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

/* ------------------------------------------------- median-cut quantization */

/**
 * Extract `count` dominant colors from a pixel source via median-cut:
 * recursively split the pixel set along the channel with the widest range,
 * at the median, until `count` boxes remain; average each box.
 * Returns dominant colors sorted by population, most-populous first.
 */
export function medianCut(imageData: PixelSource, count = 8): DominantColor[] {
  const { data } = imageData;
  const pixels: RGB[] = [];
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] > 128) pixels.push([data[i], data[i + 1], data[i + 2]]);
  }
  if (!pixels.length) return [];

  const boxes: RGB[][] = [pixels];
  while (boxes.length < count) {
    // Split the most populous box that still has spread.
    boxes.sort((a, b) => b.length - a.length);
    const box = boxes.shift()!;
    if (box.length < 2) { boxes.push(box); break; }

    let widest = 0, range = -1;
    for (let ch = 0; ch < 3; ch++) {
      let lo = 255, hi = 0;
      for (const p of box) { lo = Math.min(lo, p[ch]); hi = Math.max(hi, p[ch]); }
      if (hi - lo > range) { range = hi - lo; widest = ch; }
    }
    box.sort((a, b) => a[widest] - b[widest]);
    const mid = box.length >> 1;
    boxes.push(box.slice(0, mid), box.slice(mid));
  }

  const total = pixels.length;
  return boxes
    .map((box): DominantColor => {
      const sum = box.reduce<RGB>((s, p) => [s[0] + p[0], s[1] + p[1], s[2] + p[2]], [0, 0, 0]);
      return { rgb: [sum[0] / box.length, sum[1] / box.length, sum[2] / box.length], population: box.length / total };
    })
    .sort((a, b) => b.population - a.population);
}

/* --------------------------------------------- semantic palette derivation */

/**
 * Turn dominant colors into a semantic UI palette that satisfies the given
 * contrast targets. Camouflage principle: the page's background IS the
 * room's most present color (readability-clamped); text is whichever pole
 * clears the target contrast, tinted toward the room; the accent is the
 * room's most saturated voice.
 *
 * `options.colorScheme` forces the outcome toward a light or dark identity
 * instead of letting the room decide: the background's lightness is
 * confined to that scheme's half of the range up front, the "nudge for
 * contrast" rescue loop is only allowed to push further into that same
 * half (never crosses into the other scheme chasing a contrast target),
 * and the ink pole is fixed (dark ink on forced-light, light ink on
 * forced-dark) rather than "whichever contrasts better." Omit it for
 * today's fully automatic behavior.
 */
export function deriveSemanticPalette(
  dominants: DominantColor[],
  targets: ContrastTarget = CONTRAST_LEVELS.aaa,
  options: DeriveSemanticPaletteOptions = {},
): SemanticPalette {
  const { accentSaturationBoost = 1.25, colorScheme } = options;

  // Background: most present color, favoring calm (low-saturation) candidates.
  const bgPick = [...dominants].sort((a, b) => {
    const score = (d: DominantColor) => d.population * (1 - rgbToHsl(d.rgb)[1] * 0.5);
    return score(b) - score(a);
  })[0];
  let [bh, bs, bl] = rgbToHsl(bgPick.rgb);
  bs = Math.min(bs, 0.55);
  const [lo, hi]: [number, number] =
    colorScheme === "light" ? [0.55, 0.94]
    : colorScheme === "dark" ? [0.06, 0.45]
    : [0.14, 0.86];
  bl = clamp(bl, lo, hi);
  let bg = hslToRgb([bh, bs, bl]);

  // Mid-lightness backgrounds can't give ANY ink the target contrast —
  // nudge the background's lightness away from the middle (keeping the
  // room's hue) until one ink pole clears it. A forced scheme nudges
  // within its own half only, and stops at that half's edge rather than
  // spilling into the other scheme.
  const darkInk: RGB = [26, 26, 26], lightInk: RGB = [245, 245, 242];
  const towardLight = colorScheme === "light" ? true : colorScheme === "dark" ? false : bl >= 0.5;
  for (let i = 0;
    i < 30 && Math.max(contrast(bg, darkInk), contrast(bg, lightInk)) < targets.text;
    i++) {
    const next = clamp(bl + (towardLight ? 0.03 : -0.03), lo, hi);
    if (next === bl) break; // hit this scheme's lightness boundary
    bl = next;
    bg = hslToRgb([bh, bs, bl]);
  }
  const bgIsLight = luminance(bg) > 0.35;

  // Text: near-black or near-white. Auto picks whichever contrasts better;
  // a forced scheme fixes the pole (dark ink on light, light ink on dark)
  // so the result always reads as that scheme, not just "high contrast."
  const pole =
    colorScheme === "light" ? darkInk
    : colorScheme === "dark" ? lightInk
    : contrast(bg, darkInk) >= contrast(bg, lightInk) ? darkInk : lightInk;
  let text = mix(pole, bg, 0.08);
  if (contrast(bg, text) < targets.text) text = pole; // drop tint before readability

  // Accent: most saturated dominant that can carry meaning against bg.
  const accentPick = [...dominants]
    .sort((a, b) => rgbToHsl(b.rgb)[1] - rgbToHsl(a.rgb)[1])
    .find((d) => rgbToHsl(d.rgb)[1] > 0.12) ?? bgPick;
  let [ah, as, al] = rgbToHsl(accentPick.rgb);
  as = clamp(as * accentSaturationBoost, 0, 0.85);
  let accent = hslToRgb([ah, as, al]);
  // Nudge accent lightness away from bg until it reads.
  for (let i = 0; i < 10 && contrast(bg, accent) < targets.accent; i++) {
    al = clamp(al + (bgIsLight ? -0.07 : 0.07), 0.05, 0.95);
    accent = hslToRgb([ah, as, al]);
  }
  // Last resort: walk the accent toward the text color (which is known to
  // clear the stricter text target) until the accent target is met, keeping
  // as much of the room's hue as possible.
  for (let i = 0; i < 12 && contrast(bg, accent) < targets.accent; i++) {
    accent = mix(accent, text, 0.25);
  }

  const surface = hslToRgb([bh, Math.min(bs, 0.5), clamp(bl + (bgIsLight ? -0.05 : 0.06), 0.1, 0.9)]);

  return {
    bg: hex(bg),
    surface: hex(surface),
    text: hex(text),
    textMuted: hex(mix(text, bg, 0.35)),
    accent: hex(accent),
    border: hex(mix(text, bg, 0.72)),
    shadow: `0 2px 14px ${hex(mix(bg, [0, 0, 0], 0.75))}44`,
    dominants: dominants.map((d) => hex(d.rgb)),
  };
}
